import React, { useState, useRef, useCallback } from "react";

const PaginationControls = ({ pagination, goToPage, isDarkMode, isLoading = false }) => {
  const { page, total, totalPages, hasNext, hasPrev, limit } = pagination;

  // Draggable state
  const [position, setPosition] = useState({ x: null, y: null });
  const [isDragging, setIsDragging] = useState(false);
  const dragRef = useRef(null);
  const dragOffset = useRef({ x: 0, y: 0 });

  const handleMouseDown = useCallback((e) => {
    if (e.target.closest("button")) return;
    e.preventDefault();
    setIsDragging(true);

    const rect = dragRef.current.getBoundingClientRect();
    dragOffset.current = {
      x: e.clientX - rect.left,
      y: e.clientY - rect.top,
    };

    const handleMouseMove = (e) => {
      const newX = Math.max(0, Math.min(window.innerWidth - rect.width, e.clientX - dragOffset.current.x));
      const newY = Math.max(0, Math.min(window.innerHeight - rect.height, e.clientY - dragOffset.current.y));
      setPosition({ x: newX, y: newY });
    };

    const handleMouseUp = () => {
      setIsDragging(false);
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };

    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);
  }, []);

  // Calculate start and end items
  const startItem = total === 0 ? 0 : (page - 1) * limit + 1;
  const endItem = Math.min(page * limit, total);

  // Generate page numbers to show
  const getPageNumbers = () => {
    const pages = [];
    const maxPagesToShow = 5;

    if (totalPages <= maxPagesToShow) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      let startPage = Math.max(1, page - 2);
      let endPage = Math.min(totalPages, page + 2);

      if (startPage === 1) {
        endPage = Math.min(totalPages, maxPagesToShow);
      }
      if (endPage === totalPages) {
        startPage = Math.max(1, totalPages - maxPagesToShow + 1);
      }

      for (let i = startPage; i <= endPage; i++) {
        pages.push(i);
      }
    }

    return pages;
  };

  const pageNumbers = getPageNumbers();

  if (total === 0) return null;

  // Position styles: use custom position if set, otherwise default to bottom-right
  const positionStyles = position.x !== null
    ? { left: position.x, top: position.y }
    : { bottom: 20, right: 20 };

  return (
    <div
      ref={dragRef}
      onMouseDown={handleMouseDown}
      className="fixed z-50"
      style={{
        ...positionStyles,
        cursor: isDragging ? "grabbing" : "grab",
      }}
    >
      <div
        className={`flex items-center gap-1 px-2 py-1.5 rounded-xl border backdrop-blur-xl transition-all duration-200 ${
          isDarkMode
            ? "bg-[#1a1a1c]/90 border-white/[0.08] shadow-2xl shadow-black/50"
            : "bg-white/90 border-gray-200/80 shadow-xl shadow-gray-300/40"
        }`}
      >
        {/* Row count info */}
        <div className={`px-2.5 py-1 text-xs tabular-nums ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}>
          <span className={`font-medium ${isDarkMode ? "text-gray-200" : "text-gray-700"}`}>{startItem.toLocaleString()}</span>
          <span className="mx-0.5">–</span>
          <span className={`font-medium ${isDarkMode ? "text-gray-200" : "text-gray-700"}`}>{endItem.toLocaleString()}</span>
          <span className="mx-1">of</span>
          <span className={`font-medium ${isDarkMode ? "text-gray-200" : "text-gray-700"}`}>{total.toLocaleString()}</span>
        </div>

        {/* Divider */}
        <div className={`w-px h-5 ${isDarkMode ? "bg-white/[0.08]" : "bg-gray-200"}`} />

        {/* Navigation */}
        <div className="flex items-center gap-0.5 px-1">
          {/* First page */}
          <button
            onClick={() => goToPage(1)}
            disabled={!hasPrev || isLoading}
            className={`w-7 h-7 flex items-center justify-center rounded-lg transition-all duration-150 ${
              !hasPrev || isLoading
                ? isDarkMode ? "text-gray-600 cursor-not-allowed" : "text-gray-300 cursor-not-allowed"
                : isDarkMode ? "text-gray-400 hover:text-white hover:bg-white/[0.08]" : "text-gray-500 hover:text-gray-900 hover:bg-gray-100"
            }`}
            title="First page"
          >
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M11 19l-7-7 7-7m8 14l-7-7 7-7" />
            </svg>
          </button>

          {/* Previous */}
          <button
            onClick={() => goToPage(page - 1)}
            disabled={!hasPrev || isLoading}
            className={`w-7 h-7 flex items-center justify-center rounded-lg transition-all duration-150 ${
              !hasPrev || isLoading
                ? isDarkMode ? "text-gray-600 cursor-not-allowed" : "text-gray-300 cursor-not-allowed"
                : isDarkMode ? "text-gray-400 hover:text-white hover:bg-white/[0.08]" : "text-gray-500 hover:text-gray-900 hover:bg-gray-100"
            }`}
            title="Previous page"
          >
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 19l-7-7 7-7" />
            </svg>
          </button>

          {/* Page numbers */}
          <div className="flex items-center gap-0.5 mx-1">
            {pageNumbers.map((pageNum) => (
              <button
                key={pageNum}
                onClick={() => goToPage(pageNum)}
                disabled={isLoading}
                className={`min-w-[28px] h-7 text-xs font-medium rounded-lg transition-all duration-150 ${
                  pageNum === page
                    ? isDarkMode
                      ? "bg-white text-black"
                      : "bg-gray-900 text-white"
                    : isDarkMode
                    ? "text-gray-400 hover:text-white hover:bg-white/[0.08]"
                    : "text-gray-500 hover:text-gray-900 hover:bg-gray-100"
                } ${isLoading ? "opacity-50 cursor-not-allowed" : ""}`}
              >
                {pageNum}
              </button>
            ))}
          </div>

          {/* Next */}
          <button
            onClick={() => goToPage(page + 1)}
            disabled={!hasNext || isLoading}
            className={`w-7 h-7 flex items-center justify-center rounded-lg transition-all duration-150 ${
              !hasNext || isLoading
                ? isDarkMode ? "text-gray-600 cursor-not-allowed" : "text-gray-300 cursor-not-allowed"
                : isDarkMode ? "text-gray-400 hover:text-white hover:bg-white/[0.08]" : "text-gray-500 hover:text-gray-900 hover:bg-gray-100"
            }`}
            title="Next page"
          >
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 5l7 7-7 7" />
            </svg>
          </button>

          {/* Last page */}
          <button
            onClick={() => goToPage(totalPages)}
            disabled={!hasNext || isLoading}
            className={`w-7 h-7 flex items-center justify-center rounded-lg transition-all duration-150 ${
              !hasNext || isLoading
                ? isDarkMode ? "text-gray-600 cursor-not-allowed" : "text-gray-300 cursor-not-allowed"
                : isDarkMode ? "text-gray-400 hover:text-white hover:bg-white/[0.08]" : "text-gray-500 hover:text-gray-900 hover:bg-gray-100"
            }`}
            title="Last page"
          >
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M13 5l7 7-7 7M5 5l7 7-7 7" />
            </svg>
          </button>
        </div>
      </div>
    </div>
  );
};

export default PaginationControls;
